import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useNavigate } from '@tanstack/react-router';
import {
  Activity, ArrowRightLeft, Bot, CornerDownLeft, Database, ExternalLink, FileText,
  ImageIcon, Inbox, LayoutDashboard, LogOut, Mail, Monitor, Moon, Plus, Search,
  Send, Settings, Sun, Tags, User, Users, Webhook,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useCommandPalette } from '@/lib/command-palette';
import { config } from '@/lib/config';
import { useAuth } from '@/lib/auth';
import { setTheme } from '@/lib/theme';
import { cn } from '@/lib/cn';

interface Command {
  id: string;
  label: string;
  group: string;
  icon: React.ComponentType<{ className?: string }>;
  keywords?: string;
  run: () => void;
}

export function CommandPalette() {
  const { open, setOpen, toggle } = useCommandPalette();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const siteUrl = config.frontendUrl;

  // Atajo global ⌘K / Ctrl+K.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        toggle();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle]);

  // Reinicia el estado al abrir.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
    }
  }, [open]);

  const commands = useMemo<Command[]>(() => {
    const go = (fn: () => void): (() => void) => () => {
      fn();
      setOpen(false);
    };
    return [
      { id: 'nav-dashboard', group: 'Navegación', label: 'Escritorio', icon: LayoutDashboard, keywords: 'inicio dashboard home', run: go(() => navigate({ to: '/' })) },
      { id: 'nav-pages', group: 'Navegación', label: 'Páginas', icon: FileText, keywords: 'pages', run: go(() => navigate({ to: '/pages' })) },
      { id: 'nav-content', group: 'Navegación', label: 'Contenidos', icon: Database, keywords: 'posts blog post-types', run: go(() => navigate({ to: '/post-types' })) },
      { id: 'nav-taxonomies', group: 'Navegación', label: 'Taxonomías', icon: Tags, keywords: 'categorias etiquetas tags', run: go(() => navigate({ to: '/taxonomies' })) },
      { id: 'nav-media', group: 'Navegación', label: 'Multimedia', icon: ImageIcon, keywords: 'media imagenes archivos', run: go(() => navigate({ to: '/media' })) },
      { id: 'nav-forms', group: 'Navegación', label: 'Formularios', icon: Mail, keywords: 'forms', run: go(() => navigate({ to: '/forms' })) },
      { id: 'nav-messages', group: 'Navegación', label: 'Mensajes', icon: Inbox, keywords: 'bandeja contacto', run: go(() => navigate({ to: '/messages' })) },
      { id: 'nav-newsletter', group: 'Navegación', label: 'Newsletter', icon: Send, keywords: 'suscriptores boletin', run: go(() => navigate({ to: '/newsletter' })) },
      { id: 'nav-leads', group: 'Navegación', label: 'IA · Leads', icon: Bot, keywords: 'chatbot ia leads', run: go(() => navigate({ to: '/ai/leads' })) },
      { id: 'nav-integrations', group: 'Navegación', label: 'Integraciones', icon: Webhook, keywords: 'crm webhook', run: go(() => navigate({ to: '/integrations' })) },
      { id: 'nav-redirects', group: 'Navegación', label: 'Redirecciones', icon: ArrowRightLeft, keywords: 'redirect 301', run: go(() => navigate({ to: '/redirects' })) },
      { id: 'nav-activity', group: 'Navegación', label: 'Actividad', icon: Activity, keywords: 'logs auditoria', run: go(() => navigate({ to: '/activity' })) },
      { id: 'nav-users', group: 'Navegación', label: 'Usuarios', icon: Users, keywords: 'cuentas administradores', run: go(() => navigate({ to: '/users' })) },
      { id: 'nav-settings', group: 'Navegación', label: 'Ajustes del sitio', icon: Settings, keywords: 'configuracion seo analitica', run: go(() => navigate({ to: '/settings' })) },
      { id: 'nav-profile', group: 'Navegación', label: 'Mi perfil', icon: User, keywords: 'cuenta perfil', run: go(() => navigate({ to: '/profile' })) },

      { id: 'new-page', group: 'Crear', label: 'Nueva página', icon: Plus, keywords: 'crear pagina', run: go(() => navigate({ to: '/pages/$id', params: { id: 'new' } })) },
      { id: 'new-media', group: 'Crear', label: 'Subir multimedia', icon: ImageIcon, keywords: 'subir archivo imagen', run: go(() => navigate({ to: '/media' })) },

      { id: 'theme-light', group: 'Apariencia', label: 'Tema: claro', icon: Sun, keywords: 'light claro', run: go(() => setTheme('light')) },
      { id: 'theme-dark', group: 'Apariencia', label: 'Tema: oscuro', icon: Moon, keywords: 'dark oscuro noche', run: go(() => setTheme('dark')) },
      { id: 'theme-system', group: 'Apariencia', label: 'Tema: sistema', icon: Monitor, keywords: 'system auto', run: go(() => setTheme('system')) },

      { id: 'view-site', group: 'General', label: 'Ver sitio', icon: ExternalLink, keywords: 'web frontend', run: go(() => window.open(siteUrl, '_blank', 'noopener,noreferrer')) },
      { id: 'logout', group: 'General', label: 'Cerrar sesión', icon: LogOut, keywords: 'salir logout', run: go(() => void logout()) },
    ];
  }, [navigate, logout, setOpen, siteUrl]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) =>
      `${c.label} ${c.keywords ?? ''} ${c.group}`.toLowerCase().includes(q),
    );
  }, [commands, query]);

  // Mantiene activeIndex dentro de rango al filtrar.
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(0, filtered.length - 1)));
  }, [filtered.length]);

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % Math.max(1, filtered.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + filtered.length) % Math.max(1, filtered.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      filtered[activeIndex]?.run();
    }
  }

  // Lleva el elemento activo al viewport de la lista.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  let lastGroup = '';

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-[14vh] z-[100] w-[92vw] max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-lg data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
          onKeyDown={onKeyDown}
        >
          <DialogPrimitive.Title className="sr-only">Paleta de comandos</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Busca y ejecuta acciones del panel.
          </DialogPrimitive.Description>

          <div className="flex items-center gap-2 border-b border-neutral-200 px-3">
            <Search className="size-4 shrink-0 text-neutral-400" />
            {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar acciones, páginas, ajustes…"
              className="h-12 w-full bg-transparent text-sm text-neutral-900 outline-none placeholder:text-neutral-400"
            />
            <kbd className="hidden shrink-0 rounded border border-neutral-200 px-1.5 py-0.5 text-[10px] font-medium text-neutral-400 sm:inline">
              ESC
            </kbd>
          </div>

          <div ref={listRef} className="max-h-[55vh] overflow-y-auto p-1.5">
            {filtered.length === 0 && (
              <p className="px-3 py-8 text-center text-sm text-neutral-400">Sin resultados.</p>
            )}
            {filtered.map((cmd, idx) => {
              const showGroup = cmd.group !== lastGroup;
              lastGroup = cmd.group;
              const Icon = cmd.icon;
              const active = idx === activeIndex;
              return (
                <div key={cmd.id}>
                  {showGroup && (
                    <div className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
                      {cmd.group}
                    </div>
                  )}
                  <button
                    type="button"
                    data-index={idx}
                    onMouseMove={() => setActiveIndex(idx)}
                    onClick={() => cmd.run()}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-left text-sm transition-colors',
                      active ? 'text-white' : 'text-neutral-700',
                    )}
                    style={active ? { background: 'linear-gradient(90deg, #66023C 0%, #AD026A 100%)' } : undefined}
                  >
                    <Icon className={cn('size-4 shrink-0', active ? 'text-white' : 'text-neutral-400')} />
                    <span className="flex-1 truncate">{cmd.label}</span>
                    {active && <CornerDownLeft className="size-3.5 text-white/70" />}
                  </button>
                </div>
              );
            })}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
